// utils/toast.js
import { toast } from "react-toastify";

// Track active toasts to prevent duplicates
const activeToasts = new Set();

// Success toast with duplicate prevention
export const showSuccess = (message) => {
  if (!message) return;

  const toastKey = `success-${message}`;
  if (activeToasts.has(toastKey)) return;

  activeToasts.add(toastKey);

  toast.success(message, {
    position: "top-right",
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "colored",
    onClose: () => activeToasts.delete(toastKey),
  });
};

// Error toast with duplicate prevention
export const showError = (message) => {
  if (!message) return;

  const toastKey = `error-${message}`;
  if (activeToasts.has(toastKey)) return;

  activeToasts.add(toastKey);

  toast.error(message, {
    position: "top-right",
    autoClose: 4000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "colored",
    onClose: () => activeToasts.delete(toastKey),
  });
};

// Info toast with duplicate prevention
export const showInfo = (message) => {
  if (!message) return;

  const toastKey = `info-${message}`;
  if (activeToasts.has(toastKey)) return;

  activeToasts.add(toastKey);

  toast.info(message, {
    position: "top-right",
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "colored",
    onClose: () => activeToasts.delete(toastKey),
  });
};

// Warning toast with duplicate prevention
export const showWarning = (message) => {
  if (!message) return;

  const toastKey = `warning-${message}`;
  if (activeToasts.has(toastKey)) return;

  activeToasts.add(toastKey);

  toast.warning(message, {
    position: "top-right",
    autoClose: 4000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "colored",
    onClose: () => activeToasts.delete(toastKey),
  });
};

// Loading toast (returns toast ID to update later)
export const showLoading = (message = "Processing...") => {
  return toast.loading(message, {
    position: "top-right",
    hideProgressBar: false,
    closeOnClick: false,
    pauseOnHover: true,
    draggable: true,
    theme: "colored",
  });
};

// Update loading toast to success
export const updateToSuccess = (toastId, message) => {
  if (!toastId) return;

  toast.update(toastId, {
    render: message,
    type: "success",
    isLoading: false,
    autoClose: 3000,
    closeOnClick: true,
  });
};

// Update loading toast to error
export const updateToError = (toastId, message) => {
  if (!toastId) return;

  toast.update(toastId, {
    render: message,
    type: "error",
    isLoading: false,
    autoClose: 4000,
    closeOnClick: true,
  });
};

// Promise toast (for async operations)
export const showPromiseToast = async (promise, messages) => {
  return toast.promise(
    promise,
    {
      pending: messages.pending || "Processing...",
      success: messages.success || "Success!",
      error: messages.error || "Error occurred!",
    },
    {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "colored",
    },
  );
};

// ✅ ADD THIS - Confirm dialog function
export const showConfirm = (title, message, options = {}) => {
  return new Promise((resolve) => {
    const result = window.confirm(`${title}\n\n${message}`);
    resolve(result);
  });
};

// Dismiss all toasts
export const dismissAll = () => {
  toast.dismiss();
  activeToasts.clear();
};

// Custom toast
export const showCustom = (message, options = {}) => {
  return toast(message, {
    position: "top-right",
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    theme: "colored",
    ...options,
  });
};
